/**
 * Customize Controls — the iPhone Control Center gesture, applied to the
 * dashboard's stat cards.
 *
 * Customize (beside the Quick Actions heading) makes the cards jiggle in place:
 * ⊖ takes one off to Quick Actions, ⊕ on a Quick Actions card brings it back,
 * and dragging a card shifts the others around it. Quick Actions doubles as the
 * "More Controls" tray — whatever is off the grid lives there, one tap away.
 *
 * The grid is a fixed 8, so customizing is a swap: remove one, add one. Done
 * stays disabled until the count is back to 8.
 *
 * Reordering runs on pointer events rather than HTML5 drag-and-drop so the same
 * code path serves mouse and touch — HTML5 dragging never fires on a phone.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

/** Ignore the jitter of a tap so a tap-to-open never reads as a drag. */
const DRAG_START_THRESHOLD_PX = 6;

const RemoveBadgeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="11" fill="currentColor" />
    <rect x="6" y="10.75" width="12" height="2.5" rx="1.25" fill="#fff" />
  </svg>
);

const AddBadgeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="11" fill="currentColor" />
    <rect x="6" y="10.75" width="12" height="2.5" rx="1.25" fill="#fff" />
    <rect x="10.75" y="6" width="2.5" height="12" rx="1.25" fill="#fff" />
  </svg>
);

const CustomizeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M4 7h9M17 7h3M4 17h3M11 17h9M15 4v6M9 14v6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * One stat card, used for both the grid and the Quick Actions tray so the two
 * are the same card in the same markup — `.card-item` / `.card-info` /
 * `.card-icon` carry every style, desktop and mobile alike.
 *
 * A div with role="button" rather than a real <button>: `<p>` and `<h2>` are
 * not valid button content, and the card has to keep the exact heading markup
 * the existing styles hang off.
 */
function DashboardControlCard({
  control,
  value,
  zone = "grid",
  isJiggling = false,
  isDragging = false,
  dragOffset,
  badge = null,
  badgeLabel,
  onBadgeClick,
  onActivate,
  onPointerDown,
  title,
  className = "",
}) {
  const dragStyle =
    isDragging && dragOffset
      ? {
          transform: `translate(${dragOffset.dx}px, ${dragOffset.dy}px) scale(1.04)`,
        }
      : undefined;

  const isInteractive = Boolean(onActivate);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onActivate(control);
  };

  return (
    <div
      className={[
        "card-item",
        "dashboard-control-card",
        isJiggling ? "is-editing" : "",
        isDragging ? "is-dragging" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-control-id={control.id}
      data-control-zone={zone}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      title={title}
      // The drag transform has to be inline to beat the jiggle keyframes.
      style={
        dragStyle
          ? { cursor: "grabbing", ...dragStyle }
          : {
              cursor: isJiggling ? "grab" : isInteractive ? "pointer" : "default",
            }
      }
      onClick={isInteractive ? () => onActivate(control) : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onPointerDown={onPointerDown}
    >
      {badge ? (
        <button
          type="button"
          className={`dashboard-control-badge dashboard-control-badge--${badge}`}
          aria-label={badgeLabel}
          title={badgeLabel}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onBadgeClick(control.id);
          }}
        >
          {badge === "remove" ? <RemoveBadgeIcon /> : <AddBadgeIcon />}
        </button>
      ) : null}

      <div className="card-info">
        <p>
          {control.labelLines
            ? control.labelLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 ? <br /> : null}
                  {line}
                </React.Fragment>
              ))
            : control.label}
        </p>
        <h2>{value}</h2>
      </div>
      <div className="card-icon">
        <img src={window.getPublicUrl(control.icon)} alt="icon" />
      </div>
    </div>
  );
}

/**
 * The customize controls, which live beside the Quick Actions heading: one
 * Customize button at rest, and Reset / Cancel / Done while editing. They sit
 * with the tray rather than over the grid so there is one place to look.
 */
export function DashboardControlsActions({
  isEditing,
  isSaving,
  canSave,
  requiredControlCount,
  onStartEditing,
  onCancelEditing,
  onSaveEditing,
  onResetToDefault,
}) {
  if (!isEditing) {
    return (
      <button
        type="button"
        className="dashboard-controls-btn dashboard-controls-btn--customize"
        onClick={onStartEditing}
        title="Add, remove and rearrange dashboard cards"
      >
        <CustomizeIcon />
        Customize
      </button>
    );
  }

  return (
    <div className="dashboard-controls-toolbar-actions">
      <button
        type="button"
        className="dashboard-controls-btn dashboard-controls-btn--ghost"
        onClick={onResetToDefault}
        disabled={isSaving}
        title="Put the dashboard back to its original cards and order"
      >
        Reset
      </button>
      <button
        type="button"
        className="dashboard-controls-btn dashboard-controls-btn--ghost"
        onClick={onCancelEditing}
        disabled={isSaving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="dashboard-controls-btn dashboard-controls-btn--primary"
        onClick={onSaveEditing}
        disabled={isSaving || !canSave}
        title={
          canSave
            ? "Save this layout"
            : `Keep ${requiredControlCount} cards on the dashboard to save`
        }
      >
        {isSaving ? "Saving..." : "Done"}
      </button>
    </div>
  );
}

export default function DashboardControlsSection({
  visibleControls,
  isEditing,
  requiredControlCount,
  onOpenControl,
  onRemoveControl,
  onMoveControlTo,
  onCardWidthChange,
  getControlValue,
}) {
  // {id, dx, dy, active} while a card is under the pointer; null otherwise.
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const gridRef = useRef(null);
  const draggingId = drag?.id ?? null;

  // Publish the grid's track width so the Quick Actions tray can match it. The
  // two grids live in different-width parents, so this is the only way they can
  // agree on a card size at every breakpoint.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") return undefined;

    const measure = () => {
      const card = grid.querySelector(".dashboard-control-card");
      if (card?.offsetWidth) onCardWidthChange?.(card.offsetWidth);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [onCardWidthChange]);

  const handlePointerDown = useCallback((event, id) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragRef.current = {
      id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
    };
    setDrag({ id, dx: 0, dy: 0, active: false });
  }, []);

  useEffect(() => {
    if (!draggingId) return undefined;

    const endDrag = () => {
      dragRef.current = null;
      setDrag(null);
    };

    const handleMove = (event) => {
      const state = dragRef.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      if (!state.active && Math.hypot(dx, dy) < DRAG_START_THRESHOLD_PX) return;

      state.active = true;
      // Keeps a touch drag from scrolling the page along with the card.
      if (event.cancelable) event.preventDefault();

      const overCard = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.("[data-control-id]");
      const overId = overCard?.getAttribute("data-control-id");

      if (overId && overId !== state.id && overCard.dataset.controlZone === "grid") {
        onMoveControlTo(state.id, overId);
        // The card just moved to a new slot, so re-anchor to the pointer —
        // carrying the old delta would fling it a full cell away.
        state.startX = event.clientX;
        state.startY = event.clientY;
        setDrag({ id: state.id, dx: 0, dy: 0, active: true });
        return;
      }

      setDrag({ id: state.id, dx, dy, active: true });
    };

    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [draggingId, onMoveControlTo]);

  // A drag left mid-flight when edit mode closes would keep the card offset.
  useEffect(() => {
    if (!isEditing) {
      dragRef.current = null;
      setDrag(null);
    }
  }, [isEditing]);

  const missingCount = requiredControlCount - visibleControls.length;

  return (
    <div
      className={`dashboard-controls-section${
        isEditing ? " dashboard-controls-section--editing" : ""
      }`}
    >
      {/* The hint sits over the grid where the dragging happens; Reset /
          Cancel / Done live with Customize beside the Quick Actions heading. */}
      {isEditing ? (
        <div className="dashboard-controls-toolbar">
          <span className="dashboard-controls-hint">
            {missingCount > 0 ? (
              <strong className="dashboard-controls-hint-warning">
                Add {missingCount} more from Quick Actions to get back to{" "}
                {requiredControlCount}
              </strong>
            ) : (
              <>
                Drag to rearrange, tap
                <span className="dashboard-controls-hint-badge">
                  <RemoveBadgeIcon />
                </span>
                to swap a card out
              </>
            )}
          </span>
        </div>
      ) : null}

      <div className="dash-top-cards" ref={gridRef}>
        {visibleControls.map((control) => (
          <DashboardControlCard
            key={control.id}
            control={control}
            value={getControlValue(control)}
            zone="grid"
            isJiggling={isEditing}
            isDragging={draggingId === control.id && drag?.active}
            dragOffset={drag}
            badge={isEditing ? "remove" : null}
            badgeLabel={`Remove ${control.label} from the dashboard`}
            onBadgeClick={onRemoveControl}
            // Cards open their detail page only outside edit mode, and only
            // when that page exists.
            onActivate={!isEditing && control.route ? onOpenControl : undefined}
            onPointerDown={
              isEditing ? (event) => handlePointerDown(event, control.id) : undefined
            }
          />
        ))}

        {/* Holds the row's shape while the user picks a replacement. */}
        {isEditing && missingCount > 0
          ? Array.from({ length: missingCount }, (_, index) => (
              <div className="dashboard-control-slot" key={`empty-slot-${index}`}>
                Empty slot
              </div>
            ))
          : null}
      </div>
    </div>
  );
}

/**
 * Quick Actions doubles as the More Controls tray: every control that is off
 * the grid shows here as a full card, so removing one hides the tile without
 * losing the page behind it. In edit mode each card carries a ⊕ to put it back.
 */
export function DashboardQuickActionsList({
  controls,
  isEditing,
  isGridFull,
  cardWidth,
  onOpenControl,
  onAddControl,
  getControlValue,
}) {
  if (!controls.length) {
    return (
      <div className="quick-actions quick-actions--single">
        <div className="quick-btn quick-btn--placeholder">
          Every control is on your dashboard.
        </div>
      </div>
    );
  }

  return (
    <div
      className="dashboard-quick-cards"
      // Tracks sized to a dashboard card, so a Quick Actions card is exactly as
      // wide as the ones above it. Falls back to the CSS default until measured.
      style={
        cardWidth
          ? {
              gridTemplateColumns: `repeat(auto-fill, ${cardWidth}px)`,
              // Same measurement handed to CSS, so the phone rule can centre a
              // lone tray card on its own track without resizing it.
              "--dashboard-quick-card-width": `${cardWidth}px`,
            }
          : undefined
      }
    >
      {controls.map((control) => {
        // The grid is a fixed 8, so a card can only come back once one has
        // gone out. Until then the ⊕ is there but says why it won't fire.
        const canAdd = isEditing && !isGridFull;
        const canOpen = !isEditing && Boolean(control.route);

        return (
          <DashboardControlCard
            key={control.id}
            control={control}
            value={getControlValue(control)}
            zone="tray"
            className={[
              "dashboard-quick-card",
              isEditing && !canAdd ? "is-add-blocked" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            badge={isEditing ? "add" : null}
            badgeLabel={
              canAdd
                ? `Add ${control.label} to the dashboard`
                : "Remove a dashboard card first"
            }
            onBadgeClick={canAdd ? onAddControl : () => {}}
            onActivate={
              canAdd
                ? (item) => onAddControl(item.id)
                : canOpen
                ? onOpenControl
                : undefined
            }
            title={
              isEditing && isGridFull
                ? "Remove a card from the dashboard first"
                : undefined
            }
          />
        );
      })}
    </div>
  );
}
